import { showToast } from "../ui/toast";
import type { SplatViewerElement } from "../index";
import { formatLibraryBytes, formatLibrarySplats, type DemoModel } from "./models";
import { isPubliclyVisible, workLinkUrl } from "./work-mode";

const loginDialog = document.querySelector<HTMLDialogElement>("#login-dialog");
const loginForm = document.querySelector<HTMLFormElement>("#login-form");
const loginError = document.querySelector<HTMLElement>("#login-error");
const authSlot = document.querySelector<HTMLElement>("#auth-slot");
const libraryList = document.querySelector<HTMLElement>("#library-list");
const modelNote = document.querySelector<HTMLElement>("#model-note");
const libraryDrop = document.querySelector<HTMLElement>("#library-drop");
const libraryFile = document.querySelector<HTMLInputElement>("#library-file");
const libraryDir = document.querySelector<HTMLInputElement>("#library-dir");
const libraryReplace = document.querySelector<HTMLInputElement>("#library-replace");
const libraryReplaceDir = document.querySelector<HTMLInputElement>("#library-replace-dir");

let user: string | null = null;
let editing = false;
let shotId = "";
let shotRotate = false;
let shotFov = "42";
let shotSaving = false;
let replaceTarget: DemoModel | null = null;

function publishAuth(): void {
  window.dispatchEvent(new CustomEvent("splat-cms-auth", { detail: { user } }));
}
let render = (): void => {};

const PIN = `<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><circle cx="7" cy="4" r="2.1" fill="currentColor"/><path d="M7 6.1v6" stroke="currentColor" stroke-width="1.4"/></svg>`;

export function bindShowcaseCms(options: {
  refreshModels: (loadIfNeeded?: boolean) => Promise<void>;
  getCatalog: () => DemoModel[];
  applyModel: (id: string, force?: boolean) => void;
  getDefaultModel: () => string;
  setDefaultModel: (id: string) => void;
}): void {
  render = () => renderLibrary(options);
  void bootstrap();
  loginForm?.addEventListener("submit", (event) => {
    event.preventDefault();
    void submitLogin(options);
  });
  document.querySelector("#login-cancel")?.addEventListener("click", () => loginDialog?.close());
  bindDrop(libraryDrop, options);
  libraryFile?.addEventListener("change", () => {
    const files = libraryFile.files;
    if (files?.length) {
      void uploadFiles(
        [...files].map((file) => ({ file, path: uploadPath(file) })),
        options,
      );
      libraryFile.value = "";
    }
  });
  libraryDir?.addEventListener("change", () => {
    const files = libraryDir.files;
    if (files?.length) {
      void uploadFiles(
        [...files].map((file) => ({ file, path: uploadPath(file) })),
        options,
      );
      libraryDir.value = "";
    }
  });
  document.querySelector("#library-folder")?.addEventListener("click", () => {
    if (!user) {
      openLogin();
      return;
    }
    libraryDir?.click();
  });
  document.querySelector("#library-edit")?.addEventListener("click", () => {
    editing = !editing;
    if (!editing) {
      closeShot(true);
    }
    render();
  });
  document.querySelector<HTMLInputElement>("#shot-fov")?.addEventListener("input", (event) => {
    const value = (event.currentTarget as HTMLInputElement).value;
    viewerEl()?.setAttribute("fov", value);
    const out = document.querySelector("#shot-fov-out");
    if (out) {
      out.textContent = `${value}°`;
    }
  });
  document.querySelector("#shot-cancel")?.addEventListener("click", () => closeShot(true));
  document.querySelector("#shot-save")?.addEventListener("click", () => void saveShot(options));
  libraryReplace?.addEventListener("change", () => {
    const model = replaceTarget;
    const file = libraryReplace.files?.[0];
    libraryReplace.value = "";
    if (model && file) {
      void replaceModel(model, [{ file, path: file.name }], options);
    }
  });
  libraryReplaceDir?.addEventListener("change", () => {
    const model = replaceTarget;
    const files = [...(libraryReplaceDir.files ?? [])];
    libraryReplaceDir.value = "";
    if (model && files.length) {
      void replaceModel(
        model,
        files.map((file) => ({ file, path: uploadPath(file) })),
        options,
      );
    }
  });
  document.querySelector("#gallery-share-work")?.addEventListener("click", () => void copyWorkLink());
}

export function syncLibrary(): void {
  render();
}

async function bootstrap(): Promise<void> {
  user = await sessionUser();
  publishAuth();
  renderAuth();
  render();
}

async function sessionUser(): Promise<string | null> {
  try {
    const response = await fetch("/api/session", { credentials: "include" });
    if (!response.ok) {
      return null;
    }
    const payload = (await response.json()) as { user?: string | null };
    return payload.user ?? null;
  } catch {
    return null;
  }
}

function renderAuth(): void {
  if (!authSlot) {
    return;
  }
  authSlot.replaceChildren();
  if (user) {
    const who = document.createElement("span");
    who.className = "auth-who";
    who.textContent = user;
    who.removeAttribute("data-tip");
    const out = document.createElement("button");
    out.type = "button";
    out.className = "sign-out";
    out.dataset.tip = "Sign out";
    out.setAttribute("aria-label", `Sign out (${user})`);
    out.innerHTML = `${SIGN_OUT}<span class="nav-label">Sign out</span>`;
    out.addEventListener("click", () => void signOut());
    authSlot.append(who, out);
    syncGalleryShareButton();
    return;
  }
  const inn = document.createElement("button");
  inn.type = "button";
  inn.className = "nav-item";
  inn.dataset.tip = "Sign in";
  inn.setAttribute("aria-label", "Sign in");
  inn.innerHTML = `${SIGN_IN}<span class="nav-label">Sign in</span>`;
  inn.addEventListener("click", openLogin);
  authSlot.append(inn);
  syncGalleryShareButton();
}

async function copyWorkLink(): Promise<void> {
  const url = workLinkUrl();
  try {
    await navigator.clipboard.writeText(url);
    showToast("Work link copied", "success");
  } catch {
    showToast(url, "info");
  }
}

function syncGalleryShareButton(): void {
  const share = document.querySelector<HTMLButtonElement>("#gallery-share-work");
  if (share) {
    share.hidden = !user;
  }
}

const SIGN_IN = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true"><path d="M14 7V5a1 1 0 0 0-1-1H5v16h8a1 1 0 0 0 1-1v-2"/><path d="M11 12H20m0 0-3-3m3 3-3 3"/></svg>`;
const SIGN_OUT = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true"><path d="M10 7V5a1 1 0 0 1 1-1h8v16h-8a1 1 0 0 1-1-1v-2"/><path d="M13 12H4m0 0 3-3m-3 3 3 3"/></svg>`;

function openLogin(): void {
  if (loginError) {
    loginError.textContent = "";
  }
  loginDialog?.showModal();
  loginForm?.querySelector<HTMLInputElement>("[name=user]")?.focus();
}

async function readJson(response: Response): Promise<Record<string, unknown>> {
  const text = await response.text();
  if (!text) {
    return {};
  }
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function apiError(response: Response, payload: Record<string, unknown>, fallback: string): string {
  if (typeof payload.error === "string" && payload.error) {
    return payload.error;
  }
  if (response.status === 405 || response.status === 404) {
    return "Sign-in is not running on this host. The page in dist/ is only the viewer; start the library server with npm start and point the site at that process.";
  }
  return fallback;
}

async function submitLogin(options: Parameters<typeof bindShowcaseCms>[0]): Promise<void> {
  if (!loginForm) {
    return;
  }
  const data = new FormData(loginForm);
  const response = await fetch("/api/login", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user: String(data.get("user") ?? ""),
      password: String(data.get("password") ?? ""),
      remember: data.get("remember") === "on",
    }),
  });
  const payload = await readJson(response);
  if (!response.ok) {
    if (loginError) {
      loginError.textContent = apiError(response, payload, "Could not sign in.");
    }
    return;
  }
  user = typeof payload.user === "string" ? payload.user : String(data.get("user") ?? "admin");
  publishAuth();
  loginDialog?.close();
  loginForm.reset();
  const remember = loginForm.querySelector<HTMLInputElement>("[name=remember]");
  if (remember) {
    remember.checked = true;
  }
  renderAuth();
  render();
  void options.refreshModels(false);
}

async function signOut(): Promise<void> {
  await fetch("/api/logout", { method: "POST", credentials: "include" });
  user = null;
  editing = false;
  closeShot(true);
  publishAuth();
  renderAuth();
  render();
}

function viewerEl(): SplatViewerElement | null {
  return document.querySelector("splat-viewer");
}

function renderLibrary(options: Parameters<typeof bindShowcaseCms>[0]): void {
  const edit = document.querySelector<HTMLButtonElement>("#library-edit");
  if (edit) {
    edit.hidden = !user;
    edit.textContent = editing ? "Done" : "Edit";
    edit.dataset.tip = editing ? "Finish editing" : "Edit scans";
    edit.classList.toggle("is-on", editing);
  }
  if (!libraryList) {
    return;
  }
  libraryList.replaceChildren();
  libraryList.classList.toggle("is-editing", editing && !!user);
  libraryDrop?.classList.toggle("is-locked", !user);
  const catalog = options.getCatalog().filter((model) => user || isPubliclyVisible(model));
  if (catalog.length === 0) {
    const empty = document.createElement("p");
    empty.className = "library-empty";
    empty.textContent = user
      ? "Library is empty. Drop a .sog or .ssog."
      : "No scans yet.";
    libraryList.append(empty);
    return;
  }
  const openId = new URL(window.location.href).searchParams.get("model");
  for (const model of catalog) {
    const card = document.createElement("article");
    card.className = "library-card";
    if (model.hidden) {
      card.classList.add("is-hidden");
    }
    const name = document.createElement("button");
    name.type = "button";
    name.className = "library-name";
    name.dataset.id = model.id;
    name.dataset.tip = model.hidden ? `${model.label} (hidden)` : `Open ${model.label}`;
    if (openId === model.id) {
      name.classList.add("is-on");
    }
    const thumb = document.createElement("span");
    thumb.className = "library-thumb";
    if (model.thumb) {
      const img = document.createElement("img");
      img.src = model.thumb;
      img.alt = "";
      thumb.append(img);
    } else {
      const initial = document.createElement("span");
      initial.className = "library-initial";
      initial.textContent = model.label.slice(0, 1);
      thumb.append(initial);
    }
    const copy = document.createElement("span");
    copy.className = "library-copy";
    const label = document.createElement("span");
    label.className = "library-label";
    label.textContent = model.label;
    const meta = document.createElement("span");
    meta.className = "library-meta";
    const size = formatLibraryBytes(model.bytes);
    const splats = formatLibrarySplats(model.splats);
    meta.textContent = [model.hidden && user ? "Hidden" : "", size, splats].filter(Boolean).join(" · ");
    if (model.bytes > 0 || model.splats > 0) {
      meta.title = [size, model.splats > 0 ? `${model.splats.toLocaleString()} splats` : ""].filter(Boolean).join(" · ");
    }
    copy.append(label, meta);
    name.append(thumb, copy);
    name.addEventListener("click", () => options.applyModel(model.id));
    card.append(name);
    if (user && editing) {
      card.append(editPanel(model, options));
    }
    libraryList.append(card);
  }
}

function editPanel(model: DemoModel, options: Parameters<typeof bindShowcaseCms>[0]): HTMLElement {
  const panel = document.createElement("div");
  panel.className = "library-edit-panel";
  const input = document.createElement("input");
  input.type = "text";
  input.value = model.label;
  input.maxLength = 80;
  input.setAttribute("aria-label", `Name for ${model.label}`);
  const commit = () => {
    const next = input.value.replace(/\s+/g, " ").trim();
    if (next && next !== model.label) {
      void renameModel(model, next, options);
    }
  };
  input.addEventListener("change", commit);
  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      input.blur();
    }
  });
  input.dataset.tip = "Rename this scan";
  const picture = document.createElement("button");
  picture.type = "button";
  picture.textContent = "Take picture";
  picture.dataset.tip = "Capture a new thumbnail from the viewer";
  picture.addEventListener("click", () => openShot(model, options));
  const startAngle = document.createElement("button");
  startAngle.type = "button";
  startAngle.textContent = "Set start view";
  startAngle.dataset.tip = "Save zoom, angle, and position as this scan’s opening view";
  startAngle.addEventListener("click", () => void saveStartAngle(model, startAngle, options));
  const replace = document.createElement("button");
  replace.type = "button";
  const streamed = model.note.toLowerCase().endsWith("lod-meta.json");
  replace.textContent = streamed ? "Replace SSOG" : "Replace file";
  replace.dataset.tip = streamed ? "Replace this streamed scan" : "Replace the scan file";
  replace.addEventListener("click", () => {
    replaceTarget = model;
    // Streamed scans: pick an SSOG .zip (or shift-click path via unpacked folder button elsewhere).
    const input = libraryReplace;
    if (input) {
      input.value = "";
      input.click();
    }
  });
  const pin = document.createElement("button");
  pin.type = "button";
  const pinned = options.getDefaultModel() === model.id;
  pin.innerHTML = `${PIN}<span>${pinned ? "Opens on load" : "Open on load"}</span>`;
  pin.dataset.tip = pinned ? "This scan opens when the site loads" : "Make this the scan that opens on load";
  pin.classList.toggle("is-on", pinned);
  pin.addEventListener("click", () => void pinModel(model, options));
  const hide = document.createElement("button");
  hide.type = "button";
  hide.textContent = model.hidden ? "Show in collection" : "Hide from collection";
  hide.dataset.tip = model.hidden ? "Show this scan in the public collection" : "Hide this scan from visitors";
  hide.classList.toggle("is-on", model.hidden);
  hide.addEventListener("click", () => void hideModel(model, !model.hidden, options));
  const nsfw = document.createElement("button");
  nsfw.type = "button";
  nsfw.textContent = model.nsfw ? "Show on work link" : "Hide on work link";
  nsfw.dataset.tip = model.nsfw
    ? "Include this scan when someone opens the /work link"
    : "Hide this scan from the /work link (still on the main collection)";
  nsfw.classList.toggle("is-on", model.nsfw);
  nsfw.addEventListener("click", () => void nsfwModel(model, !model.nsfw, options));
  const catalog = options.getCatalog();
  const index = catalog.findIndex((entry) => entry.id === model.id);
  const up = document.createElement("button");
  up.type = "button";
  up.textContent = "Move up";
  up.dataset.tip = "Move earlier in the collection";
  up.disabled = index <= 0;
  up.addEventListener("click", () => void moveModel(model, -1, options));
  const down = document.createElement("button");
  down.type = "button";
  down.textContent = "Move down";
  down.dataset.tip = "Move later in the collection";
  down.disabled = index < 0 || index >= catalog.length - 1;
  down.addEventListener("click", () => void moveModel(model, 1, options));
  const remove = document.createElement("button");
  remove.type = "button";
  remove.className = "library-remove";
  remove.textContent = "Remove";
  remove.dataset.tip = "Delete this scan";
  remove.addEventListener("click", () => void removeModel(model, options));
  panel.append(input, picture, startAngle, replace, pin, hide, nsfw, up, down, remove);
  return panel;
}

function viewFromPayload(value: unknown): [number, number, number, number, number, number] | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const view = value as { focus?: unknown; eye?: unknown };
  if (!Array.isArray(view.focus) || !Array.isArray(view.eye) || view.focus.length < 3 || view.eye.length < 3) {
    return null;
  }
  const nums = [...view.focus.slice(0, 3), ...view.eye.slice(0, 3)].map((n) => Number(n));
  if (nums.some((n) => !Number.isFinite(n))) {
    return null;
  }
  return nums as [number, number, number, number, number, number];
}

async function saveStartAngle(
  model: DemoModel,
  button: HTMLButtonElement,
  options: Parameters<typeof bindShowcaseCms>[0],
): Promise<void> {
  const viewer = viewerEl();
  if (!viewer) {
    return;
  }
  const open = document.querySelector<HTMLButtonElement>(".library-name.is-on")?.dataset.id;
  if (open !== model.id) {
    options.applyModel(model.id);
    showToast("Open this scan, frame the view you want, then press Set start view again.", "info");
    return;
  }
  const view = viewer.getOpeningView();
  if (!view) {
    showToast("The model is still loading. Wait until you can look around, then save the view.", "info");
    return;
  }
  const yaw = viewer.getViewYaw();
  button.disabled = true;
  const previous = button.textContent;
  button.textContent = "Saving…";
  try {
    const response = await fetch("/api/library/yaw", {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: model.id,
        yaw,
        view: { focus: view.slice(0, 3), eye: view.slice(3) },
      }),
    });
    const payload = await readJson(response);
    if (!response.ok) {
      showToast(typeof payload.error === "string" ? payload.error : "Could not save that view.", "error");
      return;
    }
    const saved = viewFromPayload(payload.view) ?? view;
    viewer.setAttribute("start-view", saved.join(","));
    viewer.setAttribute("start-yaw", String(typeof payload.yaw === "number" ? payload.yaw : yaw));
    showToast("Start view saved", "success");
    button.textContent = "Saved";
    await options.refreshModels(false);
    window.setTimeout(() => {
      if (button.isConnected) {
        button.textContent = previous || "Set start view";
      }
    }, 1400);
  } finally {
    button.disabled = false;
  }
}

function openShot(model: DemoModel, options: Parameters<typeof bindShowcaseCms>[0]): void {
  const viewer = viewerEl();
  const frame = document.querySelector<HTMLElement>("#shot-frame");
  if (!viewer || !frame || shotSaving) {
    return;
  }
  const open = document.querySelector<HTMLButtonElement>(".library-name.is-on")?.dataset.id;
  if (open !== model.id) {
    options.applyModel(model.id);
    return;
  }
  shotId = model.id;
  shotRotate = viewer.hasAttribute("auto-rotate");
  shotFov = viewer.getAttribute("fov") || "42";
  viewer.removeAttribute("auto-rotate");
  const fov = document.querySelector<HTMLInputElement>("#shot-fov");
  const out = document.querySelector("#shot-fov-out");
  if (fov) {
    fov.value = shotFov;
  }
  if (out) {
    out.textContent = `${shotFov}°`;
  }
  frame.hidden = false;
}

function closeShot(restore: boolean): void {
  const frame = document.querySelector<HTMLElement>("#shot-frame");
  if (frame) {
    frame.hidden = true;
  }
  const viewer = viewerEl();
  if (restore && viewer && shotId) {
    viewer.setAttribute("fov", shotFov);
    if (shotRotate) {
      viewer.setAttribute("auto-rotate", "");
    }
  }
  shotId = "";
  shotSaving = false;
}

async function saveShot(options: Parameters<typeof bindShowcaseCms>[0]): Promise<void> {
  const viewer = viewerEl();
  const canvas = viewer?.shadowRoot?.querySelector("canvas");
  const guide = document.querySelector<HTMLElement>("#shot-window");
  if (!viewer || !canvas || !guide || !shotId || shotSaving) {
    return;
  }
  const id = shotId;
  const view = canvas.getBoundingClientRect();
  const crop = guide.getBoundingClientRect();
  if (view.width < 2 || view.height < 2) {
    return;
  }
  const nx = (crop.left - view.left) / view.width;
  const ny = (crop.top - view.top) / view.height;
  const nw = crop.width / view.width;
  const nh = crop.height / view.height;
  shotSaving = true;
  const save = document.querySelector<HTMLButtonElement>("#shot-save");
  if (save) {
    save.disabled = true;
  }
  try {
    const snap = await viewer.captureStill();
    if (!snap) {
      showToast("Could not take that picture.", "error");
      return;
    }
    const side = 512;
    const out = document.createElement("canvas");
    out.width = side;
    out.height = side;
    const sx = Math.max(0, nx * snap.width);
    const sy = Math.max(0, ny * snap.height);
    const sw = Math.min(snap.width - sx, nw * snap.width);
    const sh = Math.min(snap.height - sy, nh * snap.height);
    const span = Math.max(1, Math.min(sw, sh));
    out.getContext("2d")?.drawImage(snap, sx, sy, span, span, 0, 0, side, side);
    const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, "image/jpeg", 0.86));
    if (!blob) {
      showToast("Could not take that picture.", "error");
      return;
    }
    closeShot(true);
    const response = await fetch("/api/library/thumb", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "image/jpeg", "X-Model-Id": id },
      body: blob,
    });
    const payload = await readJson(response);
    if (!response.ok) {
      showToast(typeof payload.error === "string" ? payload.error : "Could not save that picture.", "error");
      return;
    }
    showToast("Thumbnail saved", "success");
    await options.refreshModels(false);
  } finally {
    shotSaving = false;
    if (save) {
      save.disabled = false;
    }
  }
}

async function hideModel(
  model: DemoModel,
  hidden: boolean,
  options: Parameters<typeof bindShowcaseCms>[0],
): Promise<void> {
  const response = await fetch("/api/library/hidden", {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: model.id, hidden }),
  });
  const payload = await readJson(response);
  if (!response.ok) {
    showToast(typeof payload.error === "string" ? payload.error : "Could not update that scan.", "error");
    return;
  }
  showToast(hidden ? "Scan hidden from gallery" : "Scan visible in gallery", "success");
  await options.refreshModels(false);
}

async function nsfwModel(
  model: DemoModel,
  nsfw: boolean,
  options: Parameters<typeof bindShowcaseCms>[0],
): Promise<void> {
  const response = await fetch("/api/library/nsfw", {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: model.id, nsfw }),
  });
  const payload = await readJson(response);
  if (!response.ok) {
    showToast(typeof payload.error === "string" ? payload.error : "Could not update that scan.", "error");
    return;
  }
  showToast(nsfw ? "Scan hidden on work link" : "Scan visible on work link", "success");
  await options.refreshModels(false);
}

async function moveModel(
  model: DemoModel,
  direction: -1 | 1,
  options: Parameters<typeof bindShowcaseCms>[0],
): Promise<void> {
  const ids = options.getCatalog().map((entry) => entry.id);
  const index = ids.indexOf(model.id);
  const swap = index + direction;
  if (index < 0 || swap < 0 || swap >= ids.length) {
    return;
  }
  [ids[index], ids[swap]] = [ids[swap], ids[index]];
  const response = await fetch("/api/library/order", {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ order: ids }),
  });
  const payload = await readJson(response);
  if (!response.ok) {
    showToast(typeof payload.error === "string" ? payload.error : "Could not reorder that scan.", "error");
    return;
  }
  await options.refreshModels(false);
  showToast("Scan reordered", "success");
}

async function renameModel(
  model: DemoModel,
  label: string,
  options: Parameters<typeof bindShowcaseCms>[0],
): Promise<void> {
  const response = await fetch("/api/library", {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: model.id, label }),
  });
  const payload = await readJson(response);
  if (!response.ok) {
    showToast(typeof payload.error === "string" ? payload.error : "Could not rename that scan.", "error");
    return;
  }
  await options.refreshModels(false);
  showToast("Scan renamed", "success");
}

async function replaceModel(
  model: DemoModel,
  files: Array<{ file: File; path: string }>,
  options: Parameters<typeof bindShowcaseCms>[0],
): Promise<void> {
  const kept = withStreamFolder(files).filter((item) => isKeptUpload(item.path));
  if (kept.length === 0) {
    showToast("Choose a .sog, .ply, .spz, .ssog, or an unpacked streamed folder.", "info");
    return;
  }
  const zip = kept.find((item) => isSsogArchive(item.path));
  const folder = kept.some((item) => item.path.toLowerCase().endsWith("lod-meta.json"));
  if (!window.confirm(`Replace “${model.label}”? The name and thumbnail stay.`)) {
    return;
  }
  if (modelNote) {
    modelNote.textContent = "Replacing the scan…";
  }
  const headers = {
    "X-Replace-Id": model.id,
    "X-Replace-File": encodeURIComponent(model.note),
  };
  if (zip && model.note.toLowerCase().endsWith("lod-meta.json")) {
      const response = await fetch("/api/models/replace", {
        method: "POST",
        credentials: "include",
        headers: { ...headers, "X-Filename": encodeURIComponent(zip.file.name) },
        body: zip.file,
      });
      const payload = await readJson(response);
      if (!response.ok) {
        showToast(typeof payload.error === "string" ? payload.error : "Could not replace that scan.", "error");
        return;
      }
    } else if (!folder) {
      const item = kept[0];
      const response = await fetch("/api/models/replace", {
        method: "POST",
        credentials: "include",
        headers: { ...headers, "X-Filename": encodeURIComponent(item.file.name) },
        body: item.file,
      });
      const payload = await readJson(response);
      if (!response.ok) {
        showToast(typeof payload.error === "string" ? payload.error : "Could not replace that scan.", "error");
        return;
      }
    } else {
      if (!model.note.toLowerCase().endsWith("lod-meta.json")) {
        showToast("A streamed folder or .ssog file can replace a streamed scan.", "info");
        return;
      }
      const begun = await fetch("/api/models/replace", {
        method: "POST",
        credentials: "include",
        headers: { ...headers, "X-Replace-Phase": "begin" },
      });
      if (!begun.ok) {
        const payload = await readJson(begun);
        showToast(typeof payload.error === "string" ? payload.error : "Could not replace that scan.", "error");
        return;
      }
      const ordered = [...kept].sort((a, b) => {
        const aMeta = a.path.toLowerCase().endsWith("lod-meta.json") ? 1 : 0;
        const bMeta = b.path.toLowerCase().endsWith("lod-meta.json") ? 1 : 0;
        return aMeta - bMeta;
      });
      for (const item of ordered) {
        const inner = item.path.replace(/\\/g, "/").split("/").filter(Boolean).slice(1).join("/");
        const response = await fetch("/api/models/replace", {
          method: "POST",
          credentials: "include",
          headers: { ...headers, "X-Replace-Phase": "part", "X-Filename": encodeURIComponent(inner || item.file.name) },
          body: item.file,
        });
        if (!response.ok) {
          const payload = await readJson(response);
          showToast(typeof payload.error === "string" ? payload.error : `Could not save ${item.file.name}.`, "error");
          return;
        }
      }
      const committed = await fetch("/api/models/replace", {
        method: "POST",
        credentials: "include",
        headers: { ...headers, "X-Replace-Phase": "commit" },
      });
      const payload = await readJson(committed);
      if (!committed.ok) {
        showToast(typeof payload.error === "string" ? payload.error : "Could not replace that scan.", "error");
        return;
      }
    }
  await options.refreshModels(false);
  showToast("Scan replaced", "success");
  if (new URL(window.location.href).searchParams.get("model") === model.id) {
    options.applyModel(model.id, true);
  }
}

async function pinModel(model: DemoModel, options: Parameters<typeof bindShowcaseCms>[0]): Promise<void> {
  if (options.getDefaultModel() === model.id) {
    return;
  }
  const response = await fetch("/api/defaults", {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model: model.id }),
  });
  const payload = await readJson(response);
  if (!response.ok) {
    const message = typeof payload.error === "string" ? payload.error : "";
    showToast(
      message === "No settings to save."
        ? "This host is still running the older library server, so it cannot store a default scan. Copy the current server folder up and restart the app."
        : message || "Could not save that scan as the page default.",
      "error",
    );
    return;
  }
  options.setDefaultModel(model.id);
  showToast("Default scan saved", "success");
  render();
}

async function removeModel(
  model: DemoModel,
  options: Parameters<typeof bindShowcaseCms>[0],
): Promise<void> {
  if (!window.confirm(`Remove ${model.note}?`)) {
    return;
  }
  const response = await fetch("/api/models", {
    method: "DELETE",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ file: model.note, id: model.id }),
  });
  const payload = await readJson(response);
  if (!response.ok) {
    showToast(typeof payload.error === "string" ? payload.error : apiError(response, payload, "Could not remove that file."), "error");
    return;
  }
  await options.refreshModels(true);
  showToast("Scan removed", "success");
}

function bindDrop(
  target: HTMLElement | null,
  options: Parameters<typeof bindShowcaseCms>[0],
): void {
  if (!target) {
    return;
  }
  target.addEventListener("click", () => {
    if (!user) {
      openLogin();
      return;
    }
    // File picker: .sog / .ply / .ssog. Use “Add unpacked folder” for a lod-meta directory.
    libraryFile?.click();
  });
  target.addEventListener("dragover", (event) => {
    event.preventDefault();
    target.classList.add("is-over");
  });
  target.addEventListener("dragleave", () => target.classList.remove("is-over"));
  target.addEventListener("drop", (event) => {
    event.preventDefault();
    target.classList.remove("is-over");
    if (!user) {
      openLogin();
      return;
    }
    void (async () => {
      const dropped = await collectDropped(event.dataTransfer);
      void uploadFiles(dropped, options);
    })();
  });
}

function uploadPath(file: File): string {
  return (file.webkitRelativePath || file.name).replace(/\\/g, "/");
}

function isSsogArchive(path: string): boolean {
  return /\.(ssog|zip)$/i.test(path.replace(/\\/g, "/").split("/").pop() ?? "");
}

function isKeptUpload(path: string): boolean {
  const base = path.replace(/\\/g, "/").split("/").pop()?.toLowerCase() ?? "";
  return /\.(sog|ssog|ply|spz|webp|zip)$/i.test(base) || base === "lod-meta.json" || base === "meta.json";
}

/** Streamed SOG needs a folder path. Flat picks get wrapped so the server accepts them. */
function withStreamFolder(
  files: Array<{ file: File; path: string }>,
): Array<{ file: File; path: string }> {
  const normalized = files.map((item) => ({ ...item, path: item.path.replace(/\\/g, "/") }));
  const streamed = normalized.some((item) => item.path.toLowerCase().endsWith("lod-meta.json"));
  if (!streamed) {
    return normalized;
  }
  const prefixed = normalized.find((item) => item.path.includes("/"))?.path.split("/")[0];
  const folder =
    prefixed && normalized.every((item) => !item.path.includes("/") || item.path.startsWith(`${prefixed}/`))
      ? prefixed
      : `scan-${Date.now().toString(36)}`;
  return normalized.map((item) =>
    item.path.includes("/") ? item : { ...item, path: `${folder}/${item.path}` },
  );
}

async function collectDropped(transfer: DataTransfer | null): Promise<Array<{ file: File; path: string }>> {
  const items = [...(transfer?.items ?? [])];
  const fromTree: Array<{ file: File; path: string }> = [];
  for (const item of items) {
    const entry = item.webkitGetAsEntry?.();
    if (entry) {
      await walkEntry(entry, "", fromTree);
    }
  }
  if (fromTree.length > 0) {
    return fromTree;
  }
  return [...(transfer?.files ?? [])].map((file) => ({ file, path: file.name }));
}

async function walkEntry(
  entry: FileSystemEntry,
  prefix: string,
  out: Array<{ file: File; path: string }>,
): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) => {
      (entry as FileSystemFileEntry).file(resolve, reject);
    });
    out.push({ file, path: `${prefix}${file.name}` });
    return;
  }
  if (!entry.isDirectory) {
    return;
  }
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  const children: FileSystemEntry[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (batch.length === 0) {
      break;
    }
    children.push(...batch);
  }
  for (const child of children) {
    await walkEntry(child, `${prefix}${entry.name}/`, out);
  }
}

async function uploadFiles(
  files: Array<{ file: File; path: string }>,
  options: Parameters<typeof bindShowcaseCms>[0],
): Promise<void> {
  const splat = withStreamFolder(files).filter((item) => isKeptUpload(item.path));
  if (splat.length === 0) {
    showToast("Drop a .sog, .ply, or .ssog file. Or use Add unpacked folder for a lod-meta.json directory.", "info");
    return;
  }
  const zipOnly = splat.filter((item) => isSsogArchive(item.path));
  const streamedParts = splat.filter((item) => !isSsogArchive(item.path));
  const streamed = streamedParts.some((item) => item.path.toLowerCase().endsWith("lod-meta.json"));
  if (modelNote) {
    modelNote.textContent = zipOnly.length
      ? `Unpacking ${zipOnly[0].file.name}…`
      : streamed
        ? `Saving streamed SOG… 0 / ${streamedParts.length}`
        : splat.length === 1
          ? `Saving ${splat[0].file.name}…`
          : `Saving ${splat.length} files…`;
  }
  let lastFile = "";
  // .ssog / .zip: one request — the server unpacks lod-meta.json + chunks.
  for (const item of zipOnly) {
    const response = await fetch("/api/models", {
      method: "POST",
      credentials: "include",
      headers: { "X-Filename": encodeURIComponent(item.file.name) },
      body: item.file,
    });
    const payload = await readJson(response);
    if (!response.ok) {
      const message = typeof payload.error === "string" ? payload.error : apiError(response, payload, `Could not save ${item.file.name}.`);
      showToast(message, "error");
      break;
    }
    lastFile = typeof payload.file === "string" ? payload.file : item.path;
  }
  const ordered = [...streamedParts].sort((a, b) => {
    const aMeta = a.path.toLowerCase().endsWith("lod-meta.json") ? 1 : 0;
    const bMeta = b.path.toLowerCase().endsWith("lod-meta.json") ? 1 : 0;
    return aMeta - bMeta;
  });
  let done = 0;
  for (const item of ordered) {
    const response = await fetch("/api/models", {
      method: "POST",
      credentials: "include",
      headers: { "X-Filename": encodeURIComponent(item.path) },
      body: item.file,
    });
    const payload = await readJson(response);
    if (!response.ok) {
      const message = typeof payload.error === "string" ? payload.error : apiError(response, payload, `Could not save ${item.file.name}.`);
      showToast(message, "error");
      break;
    }
    done += 1;
    if (streamed && modelNote) {
      modelNote.textContent = `Saving streamed SOG… ${done} / ${ordered.length}`;
    }
    lastFile = typeof payload.file === "string" ? payload.file : item.path;
  }
  await options.refreshModels(false);
  const uploaded = options.getCatalog().find((model) => model.note === lastFile || model.note.endsWith(`/${lastFile.split("/").pop()}`));
  const byFolder =
    streamed || lastFile.toLowerCase().endsWith("lod-meta.json")
      ? options.getCatalog().find((model) => {
          const folder =
            ordered.find((item) => item.path.toLowerCase().endsWith("lod-meta.json"))?.path.split("/")[0] ||
            lastFile.split("/")[0];
          return folder ? model.note.startsWith(`${folder}/`) || model.id === folder || model.note.includes(`/${folder}/`) : false;
        })
      : undefined;
  const open = uploaded || byFolder;
  if (open) {
    options.applyModel(open.id);
  }
  showToast("Scan added to library", "success");
}
